import { api } from "@/lib/api";
import { relativeTime } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	CheckCircle,
	Loader2,
	Mail,
	Receipt,
	RefreshCw,
} from "lucide-react";
import { useState } from "react";
import type { SyncStatus } from "./settings-types";
import {
	ResetSection,
	SyncError,
	SyncInfoRow,
	SyncMutationError,
} from "./settings-ui";

const LEX_SYNC_RANGES = [
	{ value: "0", label: "Seit letztem Sync" },
	{ value: "7", label: "Letzte 7 Tage" },
	{ value: "30", label: "Letzte 30 Tage" },
	{ value: "90", label: "Letzte 3 Monate" },
	{ value: "180", label: "Letzte 6 Monate" },
	{ value: "365", label: "Letztes Jahr" },
] as const;

// ============================================================
// Sync Controls — Gmail + LexOffice manual sync triggers
// ============================================================

export function SyncControlsSection() {
	const queryClient = useQueryClient();
	const [confirmReset, setConfirmReset] = useState<
		"gmail" | "lexoffice" | null
	>(null);
	const [lexSyncDays, setLexSyncDays] = useState("0");

	const statusQuery = useQuery({
		queryKey: ["sync-status"],
		queryFn: () => api.get<{ data: SyncStatus }>("/settings/sync-status"),
		refetchInterval: 30_000,
	});

	const emailSyncMutation = useMutation({
		mutationFn: () =>
			api.post<{ data: { ok: boolean; message: string } }>(
				"/settings/sync/emails",
			),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["sync-status"] });
			queryClient.invalidateQueries({ queryKey: ["emails"] });
			queryClient.invalidateQueries({ queryKey: ["email-stats"] });
		},
	});

	const lexSyncMutation = useMutation({
		mutationFn: (days: number) =>
			api.post<{ data: { ok: boolean; message: string } }>(
				"/settings/sync/lexoffice",
				{ days },
			),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["sync-status"] });
			queryClient.invalidateQueries({ queryKey: ["lexoffice-vouchers"] });
			queryClient.invalidateQueries({ queryKey: ["lexoffice-stats"] });
		},
	});

	const emailResetMutation = useMutation({
		mutationFn: () =>
			api.post<{ data: { ok: boolean; deleted: number; message: string } }>(
				"/settings/sync/emails/reset",
			),
		onSuccess: () => {
			setConfirmReset(null);
			queryClient.invalidateQueries({ queryKey: ["sync-status"] });
			queryClient.invalidateQueries({ queryKey: ["emails"] });
			queryClient.invalidateQueries({ queryKey: ["email-stats"] });
			queryClient.invalidateQueries({ queryKey: ["bookings"] });
		},
	});

	const lexResetMutation = useMutation({
		mutationFn: () =>
			api.post<{ data: { ok: boolean; deleted: number; message: string } }>(
				"/settings/sync/lexoffice/reset",
			),
		onSuccess: () => {
			setConfirmReset(null);
			queryClient.invalidateQueries({ queryKey: ["sync-status"] });
			queryClient.invalidateQueries({ queryKey: ["lexoffice-vouchers"] });
			queryClient.invalidateQueries({ queryKey: ["lexoffice-stats"] });
			queryClient.invalidateQueries({ queryKey: ["bookings"] });
		},
	});

	const status = statusQuery.data?.data;

	return (
		<div className="space-y-3">
			<h2 className="font-medium text-stone-700">Synchronisierung</h2>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{/* Gmail Sync Card */}
				<div className="card space-y-3">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Mail size={18} className="text-blue-600" />
							<span className="font-medium text-stone-800">Gmail</span>
						</div>
						<button
							type="button"
							disabled={emailSyncMutation.isPending}
							onClick={() => emailSyncMutation.mutate()}
							className="btn-primary !py-1.5 !px-3 text-xs"
						>
							{emailSyncMutation.isPending ? (
								<Loader2 size={12} className="animate-spin inline mr-1" />
							) : (
								<RefreshCw size={12} className="inline mr-1" />
							)}
							Jetzt synchronisieren
						</button>
					</div>

					{status && (
						<div className="space-y-2 text-sm">
							<div className="grid grid-cols-2 gap-2 text-xs">
								<SyncInfoRow
									label="Inbox zuletzt"
									value={
										status.gmail.inboxLastSync
											? relativeTime(status.gmail.inboxLastSync)
											: "Nie"
									}
								/>
								<SyncInfoRow
									label="Sent zuletzt"
									value={
										status.gmail.sentLastSync
											? relativeTime(status.gmail.sentLastSync)
											: "Nie"
									}
								/>
								<SyncInfoRow
									label="Gesamt verarbeitet"
									value={String(status.gmail.totalEmails)}
								/>
								<SyncInfoRow
									label="Letzte 24h"
									value={String(status.gmail.last24h)}
								/>
							</div>
							{status.gmail.lastError && (
								<SyncError message={status.gmail.lastError} />
							)}
						</div>
					)}

					{emailSyncMutation.isSuccess && (
						<p className="text-xs text-green-600 flex items-center gap-1">
							<CheckCircle size={12} />
							{emailSyncMutation.data.data.message}
						</p>
					)}
					{emailSyncMutation.isError && (
						<SyncMutationError error={emailSyncMutation.error} />
					)}

					<ResetSection
						service="gmail"
						confirmReset={confirmReset}
						setConfirmReset={setConfirmReset}
						mutation={emailResetMutation}
						label="E-Mail-Log und Zeitstempel zurücksetzen"
					/>
				</div>

				{/* LexOffice Sync Card */}
				<div className="card space-y-3">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Receipt size={18} className="text-orange-600" />
							<span className="font-medium text-stone-800">LexOffice</span>
						</div>
						<div className="flex items-center gap-2">
							<select
								value={lexSyncDays}
								onChange={(e) => setLexSyncDays(e.target.value)}
								className="input !py-1 !px-2 text-xs w-auto"
							>
								{LEX_SYNC_RANGES.map((r) => (
									<option key={r.value} value={r.value}>
										{r.label}
									</option>
								))}
							</select>
							<button
								type="button"
								disabled={lexSyncMutation.isPending}
								onClick={() => lexSyncMutation.mutate(Number(lexSyncDays))}
								className="btn-primary !py-1.5 !px-3 text-xs"
							>
								{lexSyncMutation.isPending ? (
									<Loader2 size={12} className="animate-spin inline mr-1" />
								) : (
									<RefreshCw size={12} className="inline mr-1" />
								)}
								Sync
							</button>
						</div>
					</div>

					{status && (
						<div className="space-y-2 text-sm">
							<div className="grid grid-cols-2 gap-2 text-xs">
								<SyncInfoRow
									label="Zuletzt"
									value={
										status.lexoffice.lastSync
											? relativeTime(status.lexoffice.lastSync)
											: "Nie"
									}
								/>
								<SyncInfoRow
									label="Belege gesamt"
									value={String(status.lexoffice.totalVouchers)}
								/>
							</div>
							{status.lexoffice.lastError && (
								<SyncError message={status.lexoffice.lastError} />
							)}
						</div>
					)}

					{lexSyncMutation.isSuccess && (
						<p className="text-xs text-green-600 flex items-center gap-1">
							<CheckCircle size={12} />
							{lexSyncMutation.data.data.message}
						</p>
					)}
					{lexSyncMutation.isError && (
						<SyncMutationError error={lexSyncMutation.error} />
					)}

					<ResetSection
						service="lexoffice"
						confirmReset={confirmReset}
						setConfirmReset={setConfirmReset}
						mutation={lexResetMutation}
						label="Alle Belege und Verknüpfungen zurücksetzen"
					/>
				</div>
			</div>
		</div>
	);
}
